import { keyboard } from '@toggle/helpers/src/constants';
import { useDisableScroll } from '@toggle/helpers/src/hooks/use-disable-scroll/useDisableScroll';
import clsx from 'clsx';
import React, {
  ForwardRefExoticComponent,
  Fragment,
  forwardRef,
  ReactNode,
  RefAttributes,
  useEffect,
  useId,
  useRef,
} from 'react';

import { Button, ButtonProps } from '../button/Button';
import { Portal } from '../portal';
import styles from './Modal.module.scss';

export const CLOSE_MODAL_ID = 'modal-close-button';
export const MODAL_ID = 'modal';

export interface ModalProps {
  isOpen: boolean;
  withBackdrop?: boolean;
  withClose?: boolean;
  outsideClickClose?: boolean;
  description?: ReactNode;
  title?: ReactNode;
  onClose?: () => void;
  children?: ReactNode;
  imageNode?: ReactNode;
  cancelBtnProps?: ButtonProps;
  secondaryBtnProps?: ButtonProps;
  primaryBtnProps?: ButtonProps;
  footerLeftAction?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  'data-testid'?: string;
  inPortal?: boolean;
  disableEnterClose?: boolean;
}

const ModalComponent = forwardRef<HTMLButtonElement, ModalProps>(
  // eslint-disable-next-line complexity
  (
    {
      withBackdrop = true,
      withClose = true,
      outsideClickClose = true,
      isOpen,
      title,
      onClose,
      description,
      children,
      cancelBtnProps,
      secondaryBtnProps,
      primaryBtnProps,
      footerLeftAction,
      imageNode,
      className,
      style,
      inPortal = true,
      disableEnterClose = false,
      ...rest
    }: ModalProps,
    ref
  ) => {
    const backdrop = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const descriptionId = useId();

    useDisableScroll(isOpen);

    useEffect(() => {
      const { current } = backdrop;

      const keyHandler = (e: KeyboardEvent) => {
        if (e.key === keyboard.esc.key) {
          onClose?.();
        } else if (
          e.key === keyboard.enter.key &&
          !disableEnterClose &&
          primaryBtnProps?.onClick &&
          !primaryBtnProps.disabled
        ) {
          e.preventDefault();
          //@ts-ignore
          primaryBtnProps.onClick();
        }
      };

      const clickHandler = (e: MouseEvent) => {
        outsideClickClose && e.target === current && onClose?.();
      };

      if (current) {
        current.addEventListener('click', clickHandler);
        window.addEventListener('keyup', keyHandler);
      }

      if (isOpen && inPortal) {
        document.querySelector('#app')?.setAttribute('inert', 'true');
      }

      return () => {
        if (current) {
          current.removeEventListener('click', clickHandler);
        }

        document.querySelector('#app')?.removeAttribute('inert');
        window.removeEventListener('keyup', keyHandler);
      };
    }, [isOpen, onClose, primaryBtnProps]);

    const content = isOpen ? (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        data-testid={rest['data-testid'] ?? 'modal-container'}
        data-modal-container
        className={clsx(styles.modalContainer, {
          [styles.modalContainerFill]: !inPortal,
        })}
        id={MODAL_ID}
      >
        <div className={styles.header} data-modal-header>
          {(title || description || imageNode) && (
            <div className={styles.textContainer} data-modal-text-container>
              {imageNode && (
                <div className={styles.imageContainer} data-modal-image>
                  {imageNode}
                </div>
              )}
              {title && (
                <h4
                  id={titleId}
                  aria-level={2}
                  className={styles.title}
                  data-testid="modal-title"
                  data-modal-title
                >
                  {title}
                </h4>
              )}
              {description && (
                <div
                  id={descriptionId}
                  className={styles.description}
                  data-modal-description
                >
                  {description}
                </div>
              )}
            </div>
          )}
          {withClose && (
            <Button
              className={styles.closeButton}
              data-testid="modal-close-button"
              data-modal-close
              id={CLOSE_MODAL_ID}
              onClick={onClose}
              iconName="Close"
              variant="empty"
              size="small"
              ref={ref}
            />
          )}
        </div>
        {children}
        {[cancelBtnProps, secondaryBtnProps, primaryBtnProps].some(Boolean) && (
          <div
            className={clsx(styles.buttonsContainer, {
              [styles.buttonsContainerWithLeftAction]: !!footerLeftAction,
            })}
            data-modal-buttons
          >
            {footerLeftAction && (
              <div className={styles.leftActionsContainer}>
                {footerLeftAction}
              </div>
            )}
            <div className={styles.rightActionsContainer}>
              {cancelBtnProps && (
                <Button
                  data-testid="modal-cancel-button"
                  variant="tertiary"
                  size="large"
                  {...cancelBtnProps}
                />
              )}
              {secondaryBtnProps && (
                <Button
                  data-testid="modal-secondary-button"
                  variant="secondary"
                  size="large"
                  {...secondaryBtnProps}
                />
              )}
              {primaryBtnProps && (
                <Button
                  data-testid="modal-primary-button"
                  variant="primary"
                  size="large"
                  {...primaryBtnProps}
                />
              )}
            </div>
          </div>
        )}
      </div>
    ) : null;

    const modalContent = (
      <div
        className={clsx(styles.container, className)}
        style={style}
        data-modal
      >
        {withBackdrop && (
          <div
            data-testid="modal-backdrop"
            data-modal-backdrop
            ref={backdrop}
            className={clsx(styles.backdrop, {
              [styles.backdropActive]: isOpen,
            })}
          />
        )}
        {content}
      </div>
    );

    return (
      <Fragment>
        {isOpen && (inPortal ? <Portal>{modalContent}</Portal> : modalContent)}
      </Fragment>
    );
  }
);

export const Modal: ForwardRefExoticComponent<
  ModalProps & RefAttributes<HTMLButtonElement>
> = ModalComponent;
